import { z } from 'zod/mini';
import useTranslation from '~/hooks/useTranslation';
import { OrderLineSchema } from '~/validation/api/packageSessionResponse';
import orderBlockStyles from '~/styles/bookingFlow/order-block.module.css';
import { useCallback, useMemo } from 'react';
import PlainCheckbox from '~/components/Form/PlainCheckbox';

type Props = {
  pax: number;
  orderLine: z.infer<typeof OrderLineSchema>;
  onUpdateOptionQuantity: (
    optionId: number,
    orderSessionRoomTypeId: number | null,
    quantities: number[],
  ) => void;
};

export default function OrderBlockLine({
  pax,
  orderLine,
  onUpdateOptionQuantity,
}: Props) {
  const translate = useTranslation();

  // Check that at least one pax has a quantity.
  const hasQuantity = orderLine.quantities.some((quantity) => quantity > 0);

  // Create an array of quantities for each pax. So for 2 pax, we get
  // something like: [0, 1].
  const quantityPerPax: number[] = useMemo(() => {
    return Array(pax).fill(0)
      .map((_, index) => orderLine.quantities[index] ?? 0);
  }, [orderLine.quantities, pax]);

  const onCheckAll = useCallback((checked: boolean) => {
    const paxQuantityValue = checked
      ? Array(pax).fill(1)
      : Array(pax).fill(0);

    paxQuantityValue.forEach((_value, index) => {
      if (orderLine.pax_disabled?.[index]) {
        paxQuantityValue[index] = 0;
      }
    });

    onUpdateOptionQuantity(
      orderLine.id,
      orderLine.order_session_room_type_id ?? null,
      paxQuantityValue,
    );
  }, [onUpdateOptionQuantity, orderLine, pax]);

  const onCheckOrderLine = useCallback((paxIndex: number, checked: boolean) => {
    const cloned = [...quantityPerPax];
    cloned[paxIndex] = checked ? 1 : 0;

    onUpdateOptionQuantity(
      orderLine.id,
      orderLine.order_session_room_type_id ?? null,
      cloned,
    );
  }, [onUpdateOptionQuantity, orderLine, quantityPerPax]);

  return (
    <div
      key={orderLine.order_session_room_type_id ? `${orderLine.id}_${orderLine.order_session_room_type_id}` : orderLine.id}
      hidden={orderLine.visible !== undefined && orderLine.visible === false}
      style={{
        opacity: orderLine.disabled !== undefined && orderLine.disabled ? 0.5 : 1,
      }}
    >
      <PlainCheckbox
        label={orderLine.name}
        labelAffix={
          orderLine.recommended && (
            <span className={orderBlockStyles.recommendedLabel}>
              {translate('bookingFlow.overview.recommended')}
            </span>
          )
        }
        checked={hasQuantity}
        readonly={orderLine.forced ?? false}
        onChange={onCheckAll}
      />

      {orderLine.per_pax && hasQuantity && (
        <div className={orderBlockStyles.optionQuantity}>
          {quantityPerPax.map((quantity, paxIndex) => {
            return (
              // eslint-disable-next-line react/no-array-index-key
              <div key={paxIndex} className={orderBlockStyles.optionQuantityItem}>
                <PlainCheckbox
                  label={translate('bookingFlow.overview.traveler', { pax: paxIndex + 1 })}
                  checked={quantity > 0}
                  disabled={orderLine.pax_disabled?.[paxIndex]}
                  onChange={(checked) => {
                    onCheckOrderLine(paxIndex, checked);
                  }}
                />
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
